// src/components/PreQuizForm.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import '../styles/lead-capture.css';

const PreQuizForm = ({ tenantId, onComplete, universityName = '' }) => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    degreeLevel: '',
    ageRange: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form steps configuration
  const steps = [
    {
      id: 'name',
      title: "👋 Let's start with your name!",
      subtitle: "We'd love to know what to call you",
      fields: ['firstName', 'lastName']
    },
    {
      id: 'degree',
      title: "🎓 What degree are you interested in?",
      subtitle: "This helps us show you the best programs",
      fields: ['degreeLevel']
    },
    {
      id: 'age',
      title: "🎂 What's your age range?",
      subtitle: "Just curious about our audience!",
      fields: ['ageRange']
    }
  ];

  const currentStep = steps[step];
  const progressPercent = Math.round(((step + 1) / steps.length) * 100);

  // Degree level options
  const degreeOptions = [
    { value: 'bachelors', label: "Bachelor's Degree", icon: '🎓', hint: '4-year undergraduate' },
    { value: 'masters', label: "Master's Degree", icon: '📚', hint: 'Graduate level' },
    { value: 'phd', label: 'Ph.D.', icon: '🔬', hint: 'Doctoral program' },
    { value: 'certificate', label: 'Certificate', icon: '📜', hint: 'Professional cert' }
  ];

  // Age range options
  const ageOptions = [
    { value: 'under-18', label: 'Under 18', icon: '🌱' },
    { value: '18-24', label: '18-24', icon: '🎯' },
    { value: '25-34', label: '25-34', icon: '🚀' },
    { value: '35-44', label: '35-44', icon: '💼' },
    { value: '45-54', label: '45-54', icon: '🌟' },
    { value: '55+', label: '55+', icon: '🎓' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const canProceed = () => {
    const currentFields = currentStep.fields;
    
    if (currentFields.includes('firstName')) {
      return formData.firstName.trim() && formData.lastName.trim();
    }
    if (currentFields.includes('degreeLevel')) {
      return formData.degreeLevel;
    }
    if (currentFields.includes('ageRange')) {
      return formData.ageRange;
    }
    
    return false;
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(
        `${API_ROOT}/api/sessions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId,
            firstName: formData.firstName,
            lastName: formData.lastName,
            degreeLevel: formData.degreeLevel,
            ageRange: formData.ageRange
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save information');
      }

      const data = await response.json();
      const sessionId = data.session?.sessionId || data.sessionId;

      if (!sessionId) {
        throw new Error('No sessionId returned from server');
      }

      console.log('[PreQuizForm] Session created:', sessionId);
      onComplete(sessionId, formData);
    } catch (err) {
      console.error('PreQuizForm error:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`pre-quiz-form-wrapper ${submitting ? 'submitting' : ''}`}>
      {/* Brand title */}
      {universityName && (
        <h1 className="mwc-brand-title" style={{ marginBottom: 16 }}>
          Why {universityName}?
        </h1>
      )}

      {/* Progress bar */}
      <div className="mwc-progress" role="region" aria-label="Form Progress">
        <div className="mwc-progress__meta">
          Step {step + 1} of {steps.length}
        </div>
        <div
          className="mwc-progress__bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={steps.length}
          aria-valuenow={step + 1}
          aria-valuetext={`${progressPercent}% complete`}
        >
          <div className="mwc-progress__fill" style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      {/* Form card */}
      <div className="mwc-card pre-quiz-form-card">
        <div className="pre-quiz-form-header">
          <h2 className="pre-quiz-form-title">{currentStep.title}</h2>
          <p className="pre-quiz-form-subtitle">{currentStep.subtitle}</p>
        </div>

        <div className="pre-quiz-form-body">
          {/* Step 0: Name */}
          {step === 0 && (
            <div className="pre-quiz-form-step" style={{ animation: 'slideIn 0.3s ease-out' }}>
              <div className="form-field">
                <label htmlFor="firstName" className="form-label">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  className="form-input"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="e.g., Alex"
                  autoFocus
                  disabled={submitting}
                />
              </div>

              <div className="form-field">
                <label htmlFor="lastName" className="form-label">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  className="form-input"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="e.g., Johnson"
                  disabled={submitting}
                />
              </div>
            </div>
          )}

          {/* Step 1: Degree Level */}
          {step === 1 && (
            <div className="pre-quiz-form-step" style={{ animation: 'slideIn 0.3s ease-out' }}>
              <ul className="mwc-question__options" role="group" aria-label="Degree level options">
                {degreeOptions.map(option => {
                  const isSelected = formData.degreeLevel === option.value;
                  return (
                    <li 
                      key={option.value} 
                      className={`mwc-option ${isSelected ? 'mwc-option--selected' : ''}`}
                    >
                      <label className="mwc-option__label" style={{ cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="degreeLevel"
                          className="mwc-option__input"
                          value={option.value}
                          checked={isSelected}
                          onChange={(e) => handleChange('degreeLevel', e.target.value)}
                          disabled={submitting}
                        />
                        <span className="mwc-option__content">
                          <span className="mwc-option__text">
                            <span style={{ marginRight: 8 }}>{option.icon}</span>
                            {option.label}
                          </span>
                          {option.hint && <span className="mwc-option__hint">{option.hint}</span>}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Step 2: Age Range */}
          {step === 2 && (
            <div className="pre-quiz-form-step" style={{ animation: 'slideIn 0.3s ease-out' }}>
              <ul className="mwc-question__options" role="group" aria-label="Age range options">
                {ageOptions.map(option => {
                  const isSelected = formData.ageRange === option.value;
                  return (
                    <li 
                      key={option.value} 
                      className={`mwc-option ${isSelected ? 'mwc-option--selected' : ''}`}
                    >
                      <label className="mwc-option__label" style={{ cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="ageRange"
                          className="mwc-option__input"
                          value={option.value}
                          checked={isSelected}
                          onChange={(e) => handleChange('ageRange', e.target.value)}
                          disabled={submitting}
                        />
                        <span className="mwc-option__content">
                          <span className="mwc-option__text">
                            <span style={{ marginRight: 8 }}>{option.icon}</span>
                            {option.label}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="mwc-quiz-controls pre-quiz-form-controls">
          {step > 0 && (
            <button
              type="button"
              className="mwc-btn"
              onClick={handleBack}
              disabled={submitting}
            >
              ← Back
            </button>
          )}

          {step < steps.length - 1 ? (
            <button
              type="button"
              className="mwc-btn mwc-btn--primary"
              onClick={handleNext}
              disabled={!canProceed() || submitting}
              style={{ marginLeft: step > 0 ? 12 : 0 }}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              className="mwc-btn mwc-btn--primary"
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              style={{ marginLeft: step > 0 ? 12 : 0 }}
            >
              {submitting ? '⏳ Starting Quiz...' : '🚀 Start Quiz!'}
            </button>
          )}
        </div>
      </div>

      {/* Fun footer message */}
      <div className="pre-quiz-form-footer">
        {step === 0 && "🎉 This will only take a minute!"}
        {step === 1 && "🔥 You're doing great!"}
        {step === 2 && "⚡ Almost ready to start!"}
      </div>

      {/* Privacy note */}
      <p className="pre-quiz-form-privacy">
        🔒 We respect your privacy. Your information helps us personalize your experience.
      </p>
    </div>
  );
};

PreQuizForm.propTypes = {
  tenantId: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  universityName: PropTypes.string
};

export default PreQuizForm;